<template>
  <div v-if="isLoading">
    <h2 :class="$style.actionTitleImage">
      {{ storeComponents.infoDropdownAction.titleActionHeader }}
    </h2>

    <div :class="$style.bannerStatus">
      <img
        :class="$style.bannerStatusBanner"
        :src="storeComponents.infoDropdownAction.mainBannerAction?.url"
        :alt="storeComponents.infoDropdownAction.mainBannerAction?.name"
      />
      <BentoActionCard
        is-type-text-button
        :class="$style.bentoActionBlockStatus"
        :text-label="
          shouldShowWinnerLabel ? winnerTextLabel : shouldShowQualifiedLabel ? 'Qualified' : ''
        "
        :is-shown-label="shouldShowWinnerLabel || shouldShowQualifiedLabel"
        :text-join="joinButtonText"
        :is-shown-cup-action="
          Boolean((isUserInWinGroup && hasActiveFinaledRound) || shouldShowWinnerLabel)
        "
        :is-shown-button="isShownButton"
        @openSignOrRacemodal="openRaceOrSignupModal"
      />
      <BentoActionCard
        :class="$style.bentoActionBlockTimer"
        is-type-text-timer
        :start-time="startTimeForAction"
        :end-time="endTimeForAction"
        :text-timer="storeComponents.infoDropdownAction.bentoTimerText"
        :duration-date="durationDate"
      />
      <BentoActionCard
        :class="$style.bentoActionBlockPrize"
        is-type-text-with-text
        :text-title="storeComponents.infoDropdownAction.bentoPrizeTitle"
        :text-prize="storeComponents.infoDropdownAction.bentoPrizeSum"
      />
      <div :class="$style.bentoActionBlockPlaces">
        <template v-if="!activeFinaledRoundTournament && !finishedFinaledRoundTournament">
          <PrizePlaceCard
            v-for="reward in dataAction.rewards"
            :key="reward.title"
            :title="reward.title"
            :image-card="reward.icon.url"
            :prize="reward.prize"
          />
        </template>
        <template v-if="finishedFinaledRoundTournament || activeFinaledRoundTournament">
          <PrizePlaceCard
            v-for="(reward, index) in dataAction.rewards"
            :key="reward.title"
            :title="nicknames[index] || reward.title"
            :image-card="reward.icon.url"
            :prize="prizes[index] || reward.prize"
          />
        </template>
      </div>
    </div>

    <div :class="$style.actionTermsWrapper">
      <div :class="$style.actionTitleImage">{{ dataAction.termsOfPromotionTitle }}</div>
      <Markdown :class="$style.actionTermsText" :text="dataAction.termsOfPromotionText" />
    </div>

    <div :class="$style.actionTitleImage">
      {{ dataAction.roundsTitle }}
    </div>
    <div :class="$style.actionsRoundWrapper">
      <CardAction
        v-for="race in tournamentsListToShow"
        :key="race.raceData.ssId"
        :race-ss-id="race.raceData.ssId"
        :data="data"
        :is-tournaments-finished="race.tournamentData.status === ERaceStatus.finished"
      />
    </div>
    <template v-if="specialEventBonuses.length >= 1">
      <div :class="$style.actionTitleImage">{{ dataAction.titleActionBonuses }}</div>
      <div :class="$style.promotionCardWrapper">
        <BonusPromotionCard
          v-for="bonus in specialEventBonuses"
          :key="bonus.bonusId"
          :bonus="bonus"
        />
      </div>
    </template>
    <h2 :class="$style.actionTermsTitme">{{ dataAction.termsAndContitionsTitle }}</h2>
    <Markdown :class="$style.actionTermsText" :text="dataAction.termsAndConditionsText" />
  </div>
  <ActionsBlockSkeleton v-else />
</template>

<script lang="ts" setup>
import BentoActionCard from '~/components/action/bentoActionCard.vue';
import CardAction from '~/components/action/cardAction.vue';
import PrizePlaceCard from '~/components/action/prizePlaceCard.vue';
import BonusPromotionCard from '~/components/bonus/promotionCard.vue';
import ActionsBlockSkeleton from '~/components/infoPage/actionsBlockSkeleton.vue';
import Markdown from '~/components/markdown.vue';
import { useSnippet } from '~/composables/useSnippet';
import { useTemplateSnippet } from '~/composables/useTemplateSnippet';
import { useStoreModals } from '~/stores/abstract/modals';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreSsTournaments } from '~/stores/softswiss/tournaments';
import { useStoreActions } from '~/stores/strapi/actions';
import { useStoreBonuses } from '~/stores/strapi/bonuses';
import { useStoreBonusesTabs } from '~/stores/strapi/bonusesTabs';
import { useStoreComponents } from '~/stores/strapi/components';
import { useStoreRaces } from '~/stores/strapi/races';
import { EBonusType } from '~/types/bonuses';
import { EModalName } from '~/types/modals';
import { ERaceStatus, ERaceType } from '~/types/races';
import { formatDate } from '~/utils/date/helpers';
import type {
  ComponentInfoPageBlocksRacesBlock,
  ComponentInfoPageBlocksActionsBlock,
} from '~/types/infoPages';

type CombinedType = ComponentInfoPageBlocksRacesBlock | ComponentInfoPageBlocksActionsBlock;

const props = defineProps({
  data: {
    type: Object as unknown as PropType<CombinedType>,
    required: true,
  },
});

const dataAction = props.data as ComponentInfoPageBlocksActionsBlock;

const storeBonuses = useStoreBonuses();
const storeActions = useStoreActions();
const storeRaces = useStoreRaces();
const snippet = useSnippet();
const templateSnippet = useTemplateSnippet();
const storeSsTournaments = useStoreSsTournaments();
const storeBonusesTabs = useStoreBonusesTabs();
const storeComponents = useStoreComponents();
const storeModals = useStoreModals();
const isLoading = ref(false);
const storeSsPlayer = useStoreSsPlayer();

const tournamentExtended = computed(() => {
  return tournamentsListToShow.value.find((t) =>
    t.tournamentData.frontend_identifier?.toLowerCase()
  );
});

const tournamentsListToShow = computed(() => {
  return storeSsTournaments.tournamentsExtended.filter(
    (tournament) => tournament.raceData.raceType === ERaceType.SpecialEvent
  );
  // TODO: Поки закомічу порядок показу турінірів бо може знову захочуть повернути
  // .sort((a, b) => {
  //   return statusOrder[a.tournamentData.status] - statusOrder[b.tournamentData.status];
  // });
});

// TODO: Поки закомічу порядок показу турінірів бо може знову захочуть повернути
// const statusOrder: { [key in ERaceStatus]: number } = {
//   [ERaceStatus.active]: 1,
//   [ERaceStatus.scheduled]: 2,
//   [ERaceStatus.finished]: 3,
//   [ERaceStatus.joined]: 0,
// };

const openRaceOrSignupModal = () => {
  storeModals.closeAllModals();

  if (storeSsPlayer.isPlayerAuthorised) {
    const activeTournament = tournamentsListToShow.value?.find(
      (tournament) => tournament.tournamentData.status === ERaceStatus.active
    );
    const raceId = activeTournament?.raceData?.ssId;

    if (raceId) {
      storeModals.openModal(EModalName.race, { [EModalName.race]: raceId });
      return;
    }
  }

  storeModals.openLoginOrSignupModal();
};

// Is the user in one of the groups a loser or not
const progressBars = computed(() => {
  return tournamentsListToShow.value.flatMap((tournament) => tournament.raceData.progressBar);
});

const isUserInWinGroup = computed(() => {
  return progressBars.value.some(
    (progressData) =>
      progressData.stageNumer !== 1 && storeSsPlayer.playerGroupIds.includes(progressData.groupId)
  );
});

const isUseInLoserGroup = computed(() => {
  return progressBars.value.some(
    (progressData) =>
      progressData.stageNumer !== 2 && storeSsPlayer.playerGroupIds.includes(progressData.groupId)
  );
});

const hasActiveFinaledRound = computed(() => {
  return tournamentsListToShow.value.some(
    (tournament) =>
      tournament.raceData.actionFinaledRound === true &&
      tournament.tournamentData.status === ERaceStatus.active
  );
});

const hasActiveRound = computed(() => {
  return tournamentsListToShow.value.some(
    (tournament) => tournament.tournamentData.status === ERaceStatus.active
  );
});

// Is the user in one of the groups a loser or not

const isTournamentActive = computed(
  () => tournamentExtended.value?.tournamentData.status === ERaceStatus.active
);

const isJoined = computed(() => {
  return Boolean(
    storeSsTournaments.tournamentsStatuses.find(
      (p) => p.frontId === tournamentExtended.value?.raceData?.ssId
    )?.status
  );
});

// Timer for BentoActionCard
const startTimeForAction = computed(() => Date.now());
const endTimeForAction = computed(() => {
  const startISO = storeComponents.infoDropdownAction.startDateAction;
  const endISO = storeComponents.infoDropdownAction.endDateAction;

  const now = Date.now();
  const startDate = startISO ? new Date(startISO).getTime() : 0;
  const endDate = endISO ? new Date(endISO).getTime() : 0;

  if (startDate && now < startDate) {
    return startDate;
  }

  return endDate || 0;
});

const activeFinaledRoundTournament = computed(() => {
  return tournamentsListToShow.value.some(
    (tournament) =>
      tournament.raceData.actionFinaledRound === true &&
      tournament.tournamentData.status === ERaceStatus.active
  );
});

const finishedFinaledRoundTournament = computed(() => {
  return tournamentsListToShow.value.some(
    (tournament) =>
      tournament.raceData.actionFinaledRound === true &&
      tournament.tournamentData.status === ERaceStatus.finished
  );
});

const activeFinalRoundTournament = computed(() => {
  return tournamentsListToShow.value.find(
    (tournament) =>
      tournament.raceData.actionFinaledRound === true &&
      tournament.tournamentData.status === ERaceStatus.active
  );
});

const finishedFinalRoundTournament = computed(() => {
  return tournamentsListToShow.value.find(
    (tournament) =>
      tournament.raceData.actionFinaledRound === true &&
      tournament.tournamentData.status === ERaceStatus.finished
  );
});

// дані таблиці лідерів для фінального раунду
const finalRoundLeaderboard = computed(() => {
  const finalRoundTournament =
    activeFinalRoundTournament.value || finishedFinalRoundTournament.value;

  if (!finalRoundTournament) {
    return [];
  }

  const tournament = finalRoundTournament.tournamentData;
  const customPrizeArray = finalRoundTournament?.raceData.raceTableCustom;

  const leaderboard = tournament?.top_players?.map((player, index) => {
    const playerPrize = tournament.prizes?.find(
      (prize) => prize.award_place === player.award_place
    );
    const prizes: string[] = [];

    if (playerPrize?.money_award_cents && playerPrize.money_award) {
      const newMoney =
        customPrizeArray && customPrizeArray[index] !== undefined
          ? customPrizeArray[index].prize
          : customPrizeArray && customPrizeArray[customPrizeArray.length - 1]?.prize;
      prizes.push(`${snippet.format(newMoney !== undefined ? newMoney : `0_auto_currency`)}`);
    }

    if (playerPrize?.freespins_count) {
      prizes.push(`${playerPrize?.freespins_count} FS`);
    }

    if (playerPrize?.chargeable_comp_points) {
      prizes.push(`${playerPrize?.chargeable_comp_points} CP`);
    }

    return {
      ...player,
      nickname: `${player.nickname.substring(0, 4)}****`,
      prizes,
    };
  });

  return leaderboard ? leaderboard.slice().sort((a, b) => a.award_place - b.award_place) : [];
});

const nicknames = computed(() => {
  return finalRoundLeaderboard.value.slice(0, 3).map((entry) => entry.nickname);
});

const prizes = computed(() => {
  return finalRoundLeaderboard.value.slice(0, 3).map((entry) => entry.prizes[0] || '--');
});

const specialEventBonuses = computed(() => {
  return storeBonuses.allBonusesWithStatuses.filter(
    (bonus) => bonus.bonusType === EBonusType.SpecialEvent
  );
});

const activeTournamentRound = computed(() => {
  const activeTournament = tournamentsListToShow.value?.find(
    (tournament) => tournament.tournamentData.status === ERaceStatus.active
  );

  if (!activeTournament) {
    return null;
  }

  const sortedTournaments = [...tournamentsListToShow.value].sort(
    (a, b) => a.raceData.position - b.raceData.position
  );

  const activeIndex = sortedTournaments.findIndex(
    (tournament) => tournament.raceData.ssId === activeTournament.raceData.ssId
  );

  return activeIndex >= 0 ? activeIndex + 1 : null;
});

// поточна позицію користувача у фінальному раунді
const currentUserPositionInFinalRound = computed(() => {
  const finalRoundTournament =
    activeFinalRoundTournament.value || finishedFinalRoundTournament.value;

  if (!finalRoundTournament) {
    return null;
  }

  const currentPlayer = storeSsTournaments.tournamentStatusCache?.find((t) => {
    if (t && t.tournament_id && finalRoundTournament.tournamentData) {
      return t.tournament_id === finalRoundTournament.tournamentData.id;
    }
    return false;
  });

  const currentPlayerFromStatus =
    currentPlayer ||
    storeSsTournaments.tournamentStatus?.find((t) => {
      if (t && t.tournament_id && finalRoundTournament.tournamentData) {
        return t.tournament_id === finalRoundTournament.tournamentData.id;
      }
      return false;
    });

  if (!currentPlayerFromStatus || !currentPlayerFromStatus.award_place) {
    return null;
  }
  return currentPlayerFromStatus.award_place;
});

const totalParticipantsInFinalRound = computed(() => {
  const finalRoundTournament =
    activeFinalRoundTournament.value || finishedFinalRoundTournament.value;

  if (!finalRoundTournament) {
    return null;
  }

  const tournament = finalRoundTournament.tournamentData;
  return tournament?.top_players?.length || 0;
});

// Функція для отримання призу користувача за його місце
const getUserPrizeForPosition = (position: number | null) => {
  if (!position || !finishedFinalRoundTournament.value) {
    return null;
  }

  const finalRoundTournament = finishedFinalRoundTournament.value;

  if (!finalRoundTournament) {
    return null;
  }

  const tournament = finalRoundTournament.tournamentData;
  const playerPrize = tournament?.prizes?.find((prize) => prize.award_place === position);

  if (!playerPrize) {
    return null;
  }

  const prizes: string[] = [];

  if (playerPrize?.money_award_cents && playerPrize.money_award) {
    prizes.push(`${snippet.format(playerPrize.money_award)}`);
  }

  if (playerPrize?.freespins_count) {
    prizes.push(`${playerPrize?.freespins_count}FS`);
  }

  if (playerPrize?.chargeable_comp_points) {
    prizes.push(`${playerPrize?.chargeable_comp_points}CP`);
  }

  return prizes.length > 0 ? prizes.join(' ') : null;
};

const getRoundText = (roundNumber: number | null) => {
  if (!roundNumber) {
    return '1';
  }

  switch (roundNumber) {
    case 1:
      return '1';
    case 2:
      return '2';
    case 3:
      return '3';
    default:
      return `${roundNumber}`;
  }
};

const joinButtonText = computed(() => {
  const roundText = getRoundText(activeTournamentRound.value);
  const userPosition = currentUserPositionInFinalRound.value;
  const totalParticipants = totalParticipantsInFinalRound.value;

  if (!storeSsPlayer.isPlayerAuthorised) {
    return templateSnippet.format(storeComponents.infoDropdownAction.bentoJoinText, {
      round: roundText,
    });
  } else if (
    (storeSsPlayer.isPlayerAuthorised &&
      !hasActiveRound.value &&
      !isUseInLoserGroup.value &&
      !isUserInWinGroup.value) ||
    (storeSsPlayer.isPlayerAuthorised &&
      !isUseInLoserGroup.value &&
      !isUserInWinGroup.value &&
      hasActiveFinaledRound.value)
  ) {
    return storeComponents.infoDropdownAction.statusUserInStartTournament;
  } else if (
    storeSsPlayer.isPlayerAuthorised &&
    !isJoined.value &&
    !isUseInLoserGroup.value &&
    !isUserInWinGroup.value
  ) {
    return templateSnippet.format(storeComponents.infoDropdownAction.statusUserJoinedToRound, {
      round: roundText,
    });
  } else if (isUseInLoserGroup.value && !hasActiveFinaledRound.value && hasActiveRound.value) {
    return templateSnippet.format(
      storeComponents.infoDropdownAction.successfullyParticipateAction,
      { round: roundText }
    );
  } else if (isUseInLoserGroup.value && !hasActiveFinaledRound.value && !hasActiveRound.value) {
    return storeComponents.infoDropdownAction.changeStatusUserWaitingFinalRound
      ? storeComponents.infoDropdownAction.changeStatusUserWaitingFinalRoundText
      : storeComponents.infoDropdownAction.statusUserWaitingFinalRound;
  } else if (isUseInLoserGroup.value && hasActiveFinaledRound.value) {
    return storeComponents.infoDropdownAction.qualificationIsFinished;
  } else if (
    isUserInWinGroup.value &&
    !hasActiveFinaledRound.value &&
    !finishedFinaledRoundTournament.value
  ) {
    return storeComponents.infoDropdownAction.qualificationWinnerToFinalRound;
  } else if (isUserInWinGroup.value && hasActiveFinaledRound.value) {
    if (userPosition && totalParticipants) {
      return templateSnippet.format(
        storeComponents.infoDropdownAction.statusUserWithPlaceTournament,
        {
          userPosition,
          totalParticipants,
        }
      );
    }
    return storeComponents.infoDropdownAction.statusUserNotPlaceInRound;
  } else if (isUserInWinGroup.value && finishedFinaledRoundTournament.value) {
    if (userPosition && userPosition >= 1 && userPosition <= 3) {
      return templateSnippet.format(storeComponents.infoDropdownAction.statusUserPosition, {
        userPosition,
      });
    }

    const userPrize = getUserPrizeForPosition(userPosition);

    if (userPrize && userPosition) {
      return templateSnippet.format(storeComponents.infoDropdownAction.statusUserPositionAndPrize, {
        userPosition,
        userPrize,
      });
    }
    return storeComponents.infoDropdownAction.statusQualifiedUserFinalRound;
  }
});

const isShownButton = computed(() => {
  if (!storeSsPlayer.isPlayerAuthorised) {
    return true;
  }
  if (
    storeSsPlayer.isPlayerAuthorised &&
    !isJoined.value &&
    !isUseInLoserGroup.value &&
    !isUserInWinGroup.value &&
    isTournamentActive.value
  ) {
    return true;
  }

  if (isUserInWinGroup.value && !hasActiveFinaledRound.value) {
    return false;
  }

  if (
    (isJoined.value &&
      isTournamentActive.value &&
      !isUseInLoserGroup.value &&
      !isUserInWinGroup.value) ||
    isUseInLoserGroup.value ||
    (isUserInWinGroup && hasActiveFinaledRound)
  ) {
    return false;
  }

  return true;
});

const shouldShowQualifiedLabel = computed(() => {
  return isUserInWinGroup.value && !hasActiveFinaledRound.value;
});

// чи користувач переможець і фінальний раунд закінчився
const isUserWinnerAndFinalRoundFinished = computed(() => {
  if (!isUserInWinGroup.value) {
    return false;
  }

  const finalRoundTournament = tournamentsListToShow.value.find(
    (tournament) => tournament.raceData.actionFinaledRound === true
  );

  if (!finalRoundTournament) {
    return false;
  }

  return finalRoundTournament.tournamentData.status === ERaceStatus.finished;
});

const winnerTextLabel = computed(() => {
  return isUserWinnerAndFinalRoundFinished.value ? 'Winner' : '';
});

const shouldShowWinnerLabel = computed(() => {
  if (!isUserWinnerAndFinalRoundFinished.value) {
    return false;
  }

  const userPosition = currentUserPositionInFinalRound.value;
  return userPosition && userPosition >= 1 && userPosition <= 3;
});

const durationDate = computed(() =>
  tournamentExtended.value?.tournamentData.start_at &&
  tournamentExtended.value?.tournamentData.end_at
    ? `${formatDate(new Date(tournamentExtended.value?.tournamentData.start_at))} - ${formatDate(
        new Date(tournamentExtended.value?.tournamentData.end_at)
      )}`
    : ''
);

watch(
  tournamentExtended,
  () => {
    if (tournamentExtended.value) {
      storeSsTournaments.loadPlayerStatusInTournament(tournamentExtended.value);
    }
  },
  { immediate: true }
);

watch(
  () => activeFinalRoundTournament.value || finishedFinalRoundTournament.value,
  () => {
    const finalRoundTournament =
      activeFinalRoundTournament.value || finishedFinalRoundTournament.value;
    if (finalRoundTournament) {
      storeSsTournaments.loadPlayerStatusInTournament(finalRoundTournament);
    }
  },
  { immediate: true }
);
watch(
  currentUserPositionInFinalRound,
  (newValue) => {
    console.log('currentUserPositionInFinalRound', newValue);
  },
  { immediate: true }
);
onMounted(async () => {
  await storeRaces.loadRacesData();
  // @ts-ignore
  await storeSsTournaments.loadTournaments({ force: true });

  await Promise.all([
    storeBonusesTabs.loadBonusesTabs(),
    storeActions.loadActionsData(),
    storeBonuses.loadBonusesData(),
  ]);

  await storeSsTournaments.loadPlayerStatusesInTournaments();
  await storeSsTournaments.loadTournamentHistory();

  const finalRoundTournament =
    activeFinalRoundTournament.value || finishedFinalRoundTournament.value;
  if (finalRoundTournament) {
    await storeSsTournaments.loadPlayerStatusInTournament(finalRoundTournament);
  }

  isLoading.value = true;
});
</script>

<style lang="scss" module src="./actionsBlock.scss"></style>
